import { useSubscriptionStore } from '~/stores/subscription';
import { useTimeStore } from '~/stores/time';

import {
    subscriptionIds,
    theoryOnlyIds,
} from '~/utilities/pricing';

/**
 * @typedef {Object} CourseBasicInfo
 * @property {string} name
 * @property {string} slug
 * @property {string} icon
 * @property {Object.<string, string>} slugMap
 */
/**
 * @typedef AccessBundle
 * @property {string} access Access ID for the bundle
 * @property {string} name bundle name
 * @property {number} fullPrice full price of all accesses within the bundle
 * @property {number} [discountedPrice] bundle price with any discounts
 * @property {CourseBasicInfo[]} courses courses included in the bundle
 */
/**
 * @typedef Course
 * @property {string[]} requiredAccess
 * @property {boolean} [awaiting] is course awaiting launch
 * @property {boolean} [bought] does current user have any access to the course
 * @property {boolean} [archived] is course archived
 * @property {number} [price] course price
 * @property {boolean} [useCustomPrice] does course have custom price
 * @property {number} [minPrice] minimum price for custom price courses
 * @property {boolean} [canBeBought] can course be bought forever
 * @property {boolean} [canBeSubscribed] can course be access by subscription
 * @property {AccessBundle} [featuredBundle] featured bundle for this course
 * @property {boolean} [joined] has current user joined the course
 * @property {boolean} [closeEnroll] is joining course disabled
 * @property {boolean} [isBought] has current user bought forever course access
 */
/**
 * Is course archived
 *
 * @param {Course} course
 * @param {boolean} course.archived
 * @returns {boolean}
 */
export const courseIsArchived = (course) => {
    return !!course.archived;
};

/**
 * Is course free
 *
 * @param {Course} course
 * @param {string[]} course.requiredAccess
 * @param {number} [course.price]
 * @returns {boolean}
 */
export const courseIsFree = (course) => {
    return (!course.price || course.price == 0)
        && !course.requiredAccess?.length;
};

/**
 * Does course use custom price system
 *
 * @param {Course} course
 * @param {boolean} course.useCustomPrice
 * @param {number} course.minPrice
 * @returns {boolean}
 */
export const courseIsCustomPrice = (course) => {
    return !!course.useCustomPrice
        && !!course.minPrice;
};

/**
 * Has course been launched
 *
 * @param {Course} course
 * @param {boolean} course.awaiting
 * @returns {boolean}
 */
export const courseIsLaunched = (course) => {
    return !course.awaiting;
};

/**
 * Has course enroll been disabled
 *
 * @param {Course} course
 * @param {boolean} course.closeEnroll
 * @returns {boolean}
 */
export const courseEnrollClosed = (course) => {
    return course.closeEnroll;
};

/**
 * Does course support buying access forever
 *
 * @param {Course} course
 * @param {boolean} course.archived
 * @param {string[]} course.requiredAccess
 * @param {number} [course.price]
 * @param {boolean} [course.canBeBought]
 * @returns {boolean}
 */
export const courseHasBuyAccess = (course) => {
    if (courseIsArchived(course) || courseIsFree(course)) return false;
    if (course.canBeBought) return true;

    // Assume any non-sub tokens are buy tokens
    return course.requiredAccess?.some(accessId => !subscriptionIds.includes(accessId));
};

/**
 * Does course support access by subscription
 *
 * @param {Course} course
 * @param {boolean} course.archived
 * @param {string[]} course.requiredAccess
 * @param {number} [course.price]
 * @param {boolean} [course.canBeSubscribed]
 * @returns {boolean}
 */
export const courseHasSubAccess = (course) => {
    if (courseIsArchived(course) || courseIsFree(course)) return false;
    if (course.canBeSubscribed) return true;

    return course.requiredAccess?.some(accessId => subscriptionIds.includes(accessId));
};

/**
 * Does course have a featured bundle
 *
 * @param {Course} course
 * @param {boolean} course.archived
 * @param {string[]} course.requiredAccess
 * @param {number} [course.price]
 * @param {AccessBundle} [course.featuredBundle]
 * @param {string} [course.featuredBundle.access]
 * @returns {boolean}
 */
export const courseHasBundleAccess = (course) => {
    if (courseIsArchived(course) || courseIsFree(course)) return false;
    return !!course.featuredBundle?.access;
};

/**
 * Can course be accessed during the trial period
 *
 * @param {Course} course
 * @param {boolean} course.archived
 * @param {string[]} course.requiredAccess
 * @param {number} [course.price]
 * @param {boolean} [course.canBeSubscribed]
 * @returns {boolean}
 */
export const courseHasTrialAccess = (course) => {
    // Any course that has access via subscription
    return courseHasSubAccess(course);
};

/**
 * Does current user have course access
 *
 * @param {Course} course
 * @param {boolean} [course.bought]
 * @returns {boolean}
 */
export const currentUserHasCourseAccess = (course) => {
    if ('bought' in course) return !!course.bought;
    if (subscriptionToken()) return true;

    return false;
};

/**
 * Can current user join course
 *
 * @param {Course} course
 * @param {boolean} course.archived
 * @param {boolean} course.awaiting
 * @param {boolean} course.joined
 * @param {boolean} course.closeEnroll
 * @param {boolean} [course.bought]
 * @returns {boolean}
 */
export const currentUserCanJoinCourse = (course) => {
    if (courseIsArchived(course)) return false;
    if (!courseIsLaunched(course)) return false;
    if (!currentUserHasCourseAccess(course)) return false;
    if (courseEnrollClosed(course)) return false;

    return !course.joined;
};

/**
 * Has current user bought forever course access
 *
 * @param {Course} course
 * @param {boolean} course.bought
 * @param {boolean} [course.isBought]
 * @returns {boolean}
 */
export const currentUserHasBoughtCourse = (course) => {
    if (course.isBought) return true;

    // Assume non-sub access is buy access
    const token = subscriptionToken();

    return !token
        && !!course.bought;
};

/**
 * Has current user joined the course
 *
 * @param {Course} course
 * @param {boolean} course.joined
 * @returns {boolean}
 */
export const currentUserHasJoinedCourse = (course) => {
    return !!course.joined;
};

/**
 * @typedef AccessToken
 * @property {string} access Access Tariff id
 * @property {string} subscription Access Subscription id
 * @property {string} user User id
 * @property {number} [price] Access token price paid
 * @property {string} [grantedBy] User id of the user who has granted the access
 * @property {string} [start] ISO Date string, token period start date
 * @property {string} [end] ISO Date string, token period end date
 * @property {string} [createdViaToken] id of the AccessToken that created this token
 * @property {string} [createdViaAccess] id of the Access that created this token
 * @property {boolean} [keepStatusAfterRefund]
 * @property {string} [quickAccessForSubscription] Payment id
 */
/**
 * Get current subscription token
 *
 * @returns {AccessToken|null} access token if available, null otherwise
 */
export const subscriptionToken = () => {
    let token = useSubscriptionStore().token;

    if (token && token.end) {
        const end = new Date(token.end);
        const now = useTimeStore().now;
        if (end < now) token = null;
    }

    return token;
};

/**
 * Is current subscription token theory only
 *
 * @param {AccessToken} token
 * @param {string} token.access
 * @returns {boolean}
 */
export const subscriptionTokenTheoryOnly = (token = false) => {
    token = token || subscriptionToken();

    if (!token) return false;

    return token.access
        && theoryOnlyIds.includes(token.access);
};
